import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HeroCarousel from '../components/HeroCarousel';
import MovieCard from '../components/MovieCard';
import MovieCarousel from '../components/MovieCarousel';
import SalonCarousel from '../components/SalonCarousel';
import { moviesAPI, campaignsAPI, eventsAPI, branchesAPI } from '../api';
import { ChevronLeft, ChevronRight, Calendar, Tag } from 'lucide-react';
import { Card } from '../components/ui/card';
import { useSettings } from '../contexts/SettingsContext';
import { usePageTracking } from '../hooks/useAnalytics';

const Home = () => {
  const { settings } = useSettings();
  const [nowPlayingMovies, setNowPlayingMovies] = useState([]);
  const [upcomingMovies, setUpcomingMovies] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [news, setNews] = useState([]);
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const moviesPerPage = 4;

  // Analytics tracking
  usePageTracking('Ana Sayfa');

  useEffect(() => {
    loadData();
  }, []);

  // Update title on mount and when settings change (removed - now using Helmet below)

  // Format date to DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  // Calculate days until release date
  const getDaysUntilRelease = (releaseDate) => {
    if (!releaseDate) return null;
    
    try {
      const today = new Date();
      let release;
      
      // Handle Turkish date format: "7 Kasım 2025"
      if (releaseDate.includes('Kasım') || releaseDate.includes('Ekim') || releaseDate.includes('Eylül') || releaseDate.includes('Ocak') || releaseDate.includes('Şubat') || releaseDate.includes('Mart') || releaseDate.includes('Nisan') || releaseDate.includes('Mayıs') || releaseDate.includes('Haziran') || releaseDate.includes('Temmuz') || releaseDate.includes('Ağustos') || releaseDate.includes('Aralık')) {
        // Convert Turkish months to English
        const turkishToEnglish = {
          'Ocak': 'January', 'Şubat': 'February', 'Mart': 'March',
          'Nisan': 'April', 'Mayıs': 'May', 'Haziran': 'June',
          'Temmuz': 'July', 'Ağustos': 'August', 'Eylül': 'September',
          'Ekim': 'October', 'Kasım': 'November', 'Aralık': 'December'
        };
        
        let englishDate = releaseDate;
        Object.keys(turkishToEnglish).forEach(turkishMonth => {
          englishDate = englishDate.replace(turkishMonth, turkishToEnglish[turkishMonth]);
        });
        
        release = new Date(englishDate);
      } else {
        release = new Date(releaseDate);
      }
      
      // Check if date is valid
      if (isNaN(release.getTime())) {
        return null;
      }
      
      // Remove time part for accurate day calculation
      today.setHours(0, 0, 0, 0);
      release.setHours(0, 0, 0, 0);
      
      const diffTime = release - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    } catch (error) {
      console.error('Date calculation error:', error);
      return null;
    }
  };

  const loadData = async () => {
    try {
      const [nowPlayingRes, upcomingRes, campaignsRes, newsRes, salonsRes] = await Promise.all([
        moviesAPI.getWithShowtimes(),  // Aktif seansı olan filmler
        moviesAPI.getAll({ status: 'upcoming' }),
        campaignsAPI.getAll(),
        eventsAPI.getAll(),
        branchesAPI.getAll({ is_active: true }),
      ]);
      setNowPlayingMovies(nowPlayingRes.data);
      setUpcomingMovies(upcomingRes.data);
      setCampaigns(campaignsRes.data.slice(0, 3)); // İlk 3 kampanya
      setNews(newsRes.data.slice(0, 3)); // İlk 3 haber
      setSalons(salonsRes.data); // Tüm aktif salonlar
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter upcoming movies by release date
  const getComingSoonMovies = () => {
    return upcomingMovies.filter(movie => {
      const daysLeft = getDaysUntilRelease(movie.release_date);
      return daysLeft && daysLeft > 0 && daysLeft <= 7; // 1-7 days
    });
  };

  const getMostAwaitedMovies = () => {
    return upcomingMovies.filter(movie => {
      const daysLeft = getDaysUntilRelease(movie.release_date);
      return daysLeft && daysLeft > 7; // More than 7 days
    });
  };

  const getCurrentMovies = () => {
    const start = currentPage * moviesPerPage;
    return nowPlayingMovies.slice(start, start + moviesPerPage);
  };

  const totalPages = Math.ceil(nowPlayingMovies.length / moviesPerPage);

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <p className="text-white">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Helmet>
        <title>{settings.site_name || 'Cinefora'} - Sinema ve Film Deneyimi</title>
        <meta name="description" content={`${settings.site_name || 'Cinefora'}'da vizyondaki ve yakında vizyona girecek filmleri keşfedin. Sinema biletlerinizi online alın, kampanyalardan yararlanın.`} />
        <meta name="keywords" content="sinema, film, bilet, vizyon, cinema, movie, Cinefora" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cinefora.com.tr/" />
        <meta property="og:title" content={`${settings.site_name || 'Cinefora'} - Sinema ve Film Deneyimi`} />
        <meta property="og:description" content={`${settings.site_name || 'Cinefora'}'da vizyondaki ve yakında vizyona girecek filmleri keşfedin. Sinema biletlerinizi online alın, kampanyalardan yararlanın.`} />
        <meta property="og:image" content={settings.og_image || 'https://cinefora.com.tr/og-image.jpg'} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content={settings.site_name || 'Cinefora Sinemaları'} />
        <meta property="og:locale" content="tr_TR" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://cinefora.com.tr/" />
        <meta property="twitter:title" content={`${settings.site_name || 'Cinefora'} - Sinema ve Film Deneyimi`} />
        <meta property="twitter:description" content={`${settings.site_name || 'Cinefora'}'da vizyondaki ve yakında vizyona girecek filmleri keşfedin.`} />
        <meta property="twitter:image" content={settings.og_image || 'https://cinefora.com.tr/og-image.jpg'} />
      </Helmet>
      <Header />
      <HeroCarousel />

      {/* Now Playing Section */}
      <section className="container mx-auto px-6 py-16">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-white text-3xl font-bold">Vizyondaki Filmler</h2>
          <Link to="/vizyondaki-filmler" className="text-[#FDB714] hover:text-[#e5a510] font-semibold hidden md:inline">
            Tümünü Gör →
          </Link>
        </div>
        <MovieCarousel movies={nowPlayingMovies} autoplayDelay={4000} />
      </section>

      {/* Upcoming Movies - Coming Soon Section (1-7 days) */}
      {getComingSoonMovies().length > 0 && (
        <section className="container mx-auto px-6 py-16">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-white text-3xl font-bold">Gelecek Program</h2>
            <Link to="/yakinda" className="text-[#FDB714] hover:text-[#e5a510] font-semibold">
              Tümünü Gör →
            </Link>
          </div>
          <p className="text-gray-400 text-sm mb-6">Bu hafta Vizyonda</p>
          <MovieCarousel movies={getComingSoonMovies()} autoplayDelay={5000} />
        </section>
      )}

      {/* Most Awaited Movies Section (8+ days) */}
      {getMostAwaitedMovies().length > 0 && (
        <section className="container mx-auto px-6 py-16">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-white text-3xl font-bold">En Çok Beklenen Filmler</h2>
            <Link to="/yakinda" className="text-[#FDB714] hover:text-[#e5a510] font-semibold">
              Tümünü Gör →
            </Link>
          </div>
          <p className="text-gray-400 text-sm mb-6">Gelecek aylar için merakla beklenen filmler</p>
          <MovieCarousel movies={getMostAwaitedMovies()} autoplayDelay={6000} />
        </section>
      )}

      {/* Campaigns Section */}
      {campaigns.length > 0 && (
        <section className="container mx-auto px-6 py-16">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-white text-3xl font-bold">Kampanyaları Kaçırma!</h2>
            <Link to="/kampanyalar" className="text-[#FDB714] hover:text-[#e5a510] font-semibold">
              Tümünü Gör →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {campaigns.map((campaign) => (
              <Link key={campaign._id} to={`/kampanyalar/${campaign.slug || campaign._id}`}>
                <Card 
                  className="overflow-hidden group transition-all duration-300 h-full"
                  style={{
                    background: `linear-gradient(to bottom right, ${settings?.gradient_campaign_from || '#a855f7'}15, ${settings?.gradient_campaign_to || '#ec4899'}15)`,
                    borderColor: `${settings?.gradient_campaign_from || '#a855f7'}33`,
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `linear-gradient(to bottom right, ${settings?.gradient_campaign_from || '#a855f7'}33, ${settings?.gradient_campaign_to || '#ec4899'}33)`;
                    e.currentTarget.style.borderColor = `${settings?.gradient_campaign_from || '#a855f7'}80`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = `linear-gradient(to bottom right, ${settings?.gradient_campaign_from || '#a855f7'}15, ${settings?.gradient_campaign_to || '#ec4899'}15)`;
                    e.currentTarget.style.borderColor = `${settings?.gradient_campaign_from || '#a855f7'}33`;
                  }}
                >
                  {campaign.image && (
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={campaign.image} 
                        alt={campaign.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Tag size={18} style={{ color: settings?.gradient_campaign_from || '#a855f7' }} />
                      <span 
                        className="text-sm font-semibold bg-clip-text text-transparent"
                        style={{
                          backgroundImage: `linear-gradient(to right, ${settings?.gradient_campaign_from || '#a855f7'}, ${settings?.gradient_campaign_to || '#ec4899'})`
                        }}
                      >
                        KAMPANYA
                      </span>
                    </div>
                    <h3 
                      className="text-white font-bold text-xl mb-3 transition-all duration-300"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${settings?.gradient_campaign_from || '#a855f7'}, ${settings?.gradient_campaign_to || '#ec4899'})`;
                        e.currentTarget.style.webkitBackgroundClip = 'text';
                        e.currentTarget.style.webkitTextFillColor = 'transparent';
                        e.currentTarget.style.backgroundClip = 'text';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundImage = '';
                        e.currentTarget.style.webkitTextFillColor = 'white';
                      }}
                    >
                      {campaign.title}
                    </h3>
                    <p className="text-gray-300 text-sm line-clamp-3">{campaign.description}</p>
                    {campaign.valid_until && (
                      <p className="text-xs mt-4" style={{ color: `${settings?.gradient_campaign_from || '#a855f7'}b3` }}>
                        Son Geçerlilik: {formatDate(campaign.valid_until)}
                      </p>
                    )}
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* News Section */}
      {news.length > 0 && (
        <section className="container mx-auto px-6 py-16">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-white text-3xl font-bold">Haberler</h2>
            <Link to="/haberler" className="text-[#FDB714] hover:text-[#e5a510] font-semibold">
              Tümünü Gör →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {news.map((item) => (
              <Link key={item._id} to={`/haberler/${item.slug || item._id}`}>
                <Card 
                  className="overflow-hidden group transition-all duration-300 h-full"
                  style={{
                    background: `linear-gradient(to bottom right, ${settings?.gradient_news_from || '#3b82f6'}15, ${settings?.gradient_news_to || '#8b5cf6'}15)`,
                    borderColor: `${settings?.gradient_news_from || '#3b82f6'}33`,
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `linear-gradient(to bottom right, ${settings?.gradient_news_from || '#3b82f6'}33, ${settings?.gradient_news_to || '#8b5cf6'}33)`;
                    e.currentTarget.style.borderColor = `${settings?.gradient_news_from || '#3b82f6'}80`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = `linear-gradient(to bottom right, ${settings?.gradient_news_from || '#3b82f6'}15, ${settings?.gradient_news_to || '#8b5cf6'}15)`;
                    e.currentTarget.style.borderColor = `${settings?.gradient_news_from || '#3b82f6'}33`;
                  }}
                >
                  {item.image && (
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={item.image} 
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar size={18} style={{ color: settings?.gradient_news_from || '#3b82f6' }} />
                      <span 
                        className="text-sm font-medium bg-clip-text text-transparent"
                        style={{
                          backgroundImage: `linear-gradient(to right, ${settings?.gradient_news_from || '#3b82f6'}, ${settings?.gradient_news_to || '#8b5cf6'})`
                        }}
                      >
                        {formatDate(item.event_date)}
                      </span>
                    </div>
                    <h3 
                      className="text-white font-bold text-xl mb-3 transition-all duration-300"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${settings?.gradient_news_from || '#3b82f6'}, ${settings?.gradient_news_to || '#8b5cf6'})`;
                        e.currentTarget.style.webkitBackgroundClip = 'text';
                        e.currentTarget.style.webkitTextFillColor = 'transparent';
                        e.currentTarget.style.backgroundClip = 'text';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundImage = '';
                        e.currentTarget.style.webkitTextFillColor = 'white';
                      }}
                    >
                      {item.title}
                    </h3>
                    <p className="text-gray-300 text-sm line-clamp-3">{item.description}</p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Salonlarımız */}
      {salons.length > 0 && (
        <section className="bg-black py-16">
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-white text-3xl font-bold">Salonlarımız</h2>
              <Link to="/salonlarimiz" className="text-[#FDB714] hover:text-[#e5a510] font-semibold hidden md:inline">
                Tümünü Gör →
              </Link>
            </div>
            <SalonCarousel salons={salons} />
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default Home;
